import React, { forwardRef } from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, FormHelperText } from '@mui/material';
import PropTypes from 'prop-types';
import UiTextField from '../ui-text-field/UiTextField';
import UiChip from '../ui-chip/UiChip';
import UiIcon from '../ui-icon/UiIcon';
import { StyledUiAutocomplete } from './UiAutocomplete.styles';

const UiAutocomplete = forwardRef(
  (
    {
      name,
      control,
      loading,
      loadingText,
      noOptionsText,
      options,
      value,
      placeholder,
      ...rest
    },
    ref
  ) => {
    if (control)
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StyledUiAutocomplete className={error && 'error'}>
              <Autocomplete
                loading={loading}
                loadingText={loadingText}
                noOptionsText={noOptionsText}
                sx={{
                  '& .MuiInputBase-root': {
                    minHeight: '44px',
                    padding: '4px 0 !important',
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    display: 'none',
                  },
                  '& .MuiOutlinedInput-root': {
                    paddingRight: '8px !important',
                  },
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <UiChip
                      key={option}
                      label={option.name}
                      {...getTagProps({ index })}
                      deleteIcon={<UiIcon icon='ri-close-line' />}
                    />
                  ))
                }
                multiple
                options={options}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={options.filter((opt) => field.value.includes(opt.id))}
                onChange={(_, selected) => {
                  const ids = selected.map((item) => item.id);
                  if (ids.includes(0)) {
                    field.onChange([0]);
                  } else {
                    field.onChange(ids);
                  }
                }}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <UiTextField
                    disabled={value?.includes(0)}
                    {...params}
                    placeholder={placeholder}
                  />
                )}
                {...rest}
              />
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </StyledUiAutocomplete>
          )}
        />
      );

    return (
      <Autocomplete
        ref={ref}
        loading={loading}
        loadingText={loadingText}
        noOptionsText={noOptionsText}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <UiChip
              key={option}
              label={option.name}
              {...getTagProps({ index })}
              deleteIcon={<UiIcon icon='ri-close-line' />}
            />
          ))
        }
        multiple
        options={options}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <UiTextField
            disabled={value?.includes(0)}
            {...params}
            placeholder={placeholder}
          />
        )}
        sx={{
          '& .MuiInputBase-root': {
            minHeight: '44px',
            padding: '4px 0 !important',
          },
          '& .MuiAutocomplete-endAdornment': {
            display: 'none',
          },
          '& .MuiOutlinedInput-root': {
            paddingRight: '8px !important',
          },
        }}
        {...rest}
      />
    );
  }
);

UiAutocomplete.propTypes = {
  name: PropTypes.string,
  control: PropTypes.object,
  loading: PropTypes.bool,
  loadingText: PropTypes.string,
  noOptionsText: PropTypes.string,
  options: PropTypes.array,
  value: PropTypes.array,
  placeholder: PropTypes.string,
};

export default UiAutocomplete;
